/**
 * Hook for Yandex/manual parsing task lifecycle.
 * Extracted from useParsingWorkflow.ts (Phase 2.5 refactoring).
 *
 * Owns: taskId / progress / statusMessage / parsingResult / parsingFailed
 *       state + parsingPollRef.
 *
 * Provides: pollStatus, handleStartParsing, stopParsingPoll, resetParsing.
 *
 * Receives: configuration values (selectedCrmId, queries/regions, parser
 * settings, projects list) + the facade's setDomains / setCurrentStep so the
 * facade can react to completion. Store actions (updateTaskProgress,
 * completeTask, clearTask, setActiveTask) are passed in as well.
 */

import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { message } from 'antd'
import api from '@/services/api'
import { STEP_INDEX } from '../constants'
import { transformUrls, formatDomainsFromResult } from '../utils/parsingHelpers'
import type {
  DomainWithAnalysis,
  ParsingResult,
  QueriesRegionsData,
  ResolvedRegion,
} from '../types'
import type { DataLoadersProject } from './useDataLoaders'

interface ActiveTaskPayload {
  task_id: string
  project_id: number
  project_name: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  current_step: string
  started_at: string
}

interface UseParsingFlowProps {
  projectId?: number | null
  projectCrmId?: number | null
  projectName?: string | null
  credentialsId?: number | null
  selectedCrmId: number | null
  selectedQueries: string[]
  selectedRegionCodes: string[]
  queriesRegions: QueriesRegionsData | null
  resolvedRegions: ResolvedRegion[]
  minClicks: number
  downloadAll: boolean
  topQueries: number
  projects: DataLoadersProject[]
  autoCredentialsId: number | null
  setDomains: Dispatch<SetStateAction<DomainWithAnalysis[]>>
  setCurrentStep: (step: number) => void
  updateTaskProgress: (projectId: number, progress: number, currentStep: string) => void
  completeTask: (projectId: number) => void
  clearTask: (projectId: number) => void
  setActiveTask: (projectId: number, payload: ActiveTaskPayload) => void
}

export interface UseParsingFlowResult {
  taskId: string | null
  progress: number
  statusMessage: string
  parsingResult: ParsingResult | null
  parsingFailed: boolean
  setTaskId: Dispatch<SetStateAction<string | null>>
  setProgress: Dispatch<SetStateAction<number>>
  setStatusMessage: Dispatch<SetStateAction<string>>
  setParsingResult: Dispatch<SetStateAction<ParsingResult | null>>
  setParsingFailed: Dispatch<SetStateAction<boolean>>
  pollStatus: (taskIdToPoll: string) => Promise<void>
  handleStartParsing: (values?: { project_id?: number }) => Promise<void>
  stopParsingPoll: () => void
  resetParsing: () => void
  /** Exposed only so facade's unmount cleanup can read the timer if needed.
   *  Prefer calling stopParsingPoll(). */
  parsingPollRef: React.MutableRefObject<NodeJS.Timeout | null>
}

export function useParsingFlow({
  projectId,
  projectCrmId,
  projectName,
  credentialsId,
  selectedCrmId,
  selectedQueries,
  selectedRegionCodes,
  queriesRegions,
  resolvedRegions,
  minClicks,
  downloadAll,
  topQueries,
  projects,
  autoCredentialsId,
  setDomains,
  setCurrentStep,
  updateTaskProgress,
  completeTask,
  clearTask,
  setActiveTask,
}: UseParsingFlowProps): UseParsingFlowResult {
  const [taskId, setTaskId] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const [parsingResult, setParsingResult] = useState<ParsingResult | null>(null)
  const [parsingFailed, setParsingFailed] = useState(false)

  const parsingPollRef = useRef<NodeJS.Timeout | null>(null)

  const stopParsingPoll = useCallback(() => {
    if (parsingPollRef.current) {
      clearTimeout(parsingPollRef.current)
      parsingPollRef.current = null
    }
  }, [])

  const pollStatus = useCallback(
    async (taskIdToPoll: string) => {
      // * Idempotency guard: если parsing polling уже запущен — не стартуем дубль.
      // Защищает от повторного запуска при множественных триггерах initializeModal.
      if (parsingPollRef.current) {
        console.warn('[ParsingFlow] Parsing polling уже запущен, пропускаем дубль')
        return
      }

      const maxAttempts = 120
      let attempts = 0

      const statusTranslations: Record<string, string> = {
        pending: 'Ожидание',
        running: 'Выполняется',
        completed: 'Завершен',
        failed: 'Ошибка',
      }

      const poll = async () => {
        try {
          attempts++
          const status = await api.getParsingStatus(taskIdToPoll)

          const translatedStatus = statusTranslations[status.status] || status.status
          const currentProgress = status.progress || 0
          const currentStepMsg = status.message || `Статус: ${translatedStatus}`

          setProgress(currentProgress)
          setStatusMessage(currentStepMsg)

          if (projectId) {
            updateTaskProgress(projectId, currentProgress, currentStepMsg)
          }

          if (status.status === 'completed') {
            message.success('Парсинг завершен!')

            try {
              const resultData = await api.getParsingResult(taskIdToPoll)
              const transformedUrls = transformUrls(resultData.urls || [])

              setParsingResult({ ...resultData, urls: transformedUrls })

              const allForAnalysis = formatDomainsFromResult(resultData, transformedUrls)
              setDomains(allForAnalysis)

              console.log(`📊 Parsing results: ${allForAnalysis.length} total sources`)

              if (allForAnalysis.length === 0) {
                message.warning('Парсинг завершен, но ничего не найдено.')
              }

              if (projectId) {
                completeTask(projectId)
              }

              setCurrentStep(STEP_INDEX.results)
              return
            } catch (error: unknown) {
              console.error('❌ Failed to get parsing result:', error)
              setParsingFailed(true)
              const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка'
              setStatusMessage(`Ошибка получения результатов: ${errMsg}`)
              message.error('Не удалось получить результаты парсинга')
              return
            }
          }

          if (status.status === 'failed') {
            setParsingFailed(true)
            setStatusMessage(status.error || 'Парсинг завершился с ошибкой')
            message.error('Парсинг завершился с ошибкой')
            return
          }

          if (attempts >= maxAttempts) {
            setParsingFailed(true)
            setStatusMessage('Превышено время ожидания')
            message.error('Превышено время ожидания')
            return
          }

          parsingPollRef.current = setTimeout(poll, 5000)
        } catch (error: unknown) {
          console.error('Error polling status:', error)

          const err = error as { response?: { status?: number }; message?: string }
          if (err.response?.status === 404 || err.message?.includes('not found')) {
            setParsingFailed(true)
            setStatusMessage('Задача не найдена или была удалена')
            message.error('Задача парсинга не найдена')
            if (projectId) {
              clearTask(projectId)
            }
            return
          }

          if (attempts >= maxAttempts) {
            setParsingFailed(true)
            setStatusMessage('Ошибка получения статуса')
            return
          }

          parsingPollRef.current = setTimeout(poll, 5000)
        }
      }

      poll()
    },
    [projectId, updateTaskProgress, completeTask, clearTask, setDomains, setCurrentStep]
  )

  const handleStartParsing = useCallback(
    async (values?: { project_id?: number }) => {
      const currentProjectId = projectId || values?.project_id

      // Check for active parsing
      if (currentProjectId) {
        try {
          const tasksData = await api.getParsingTasks()
          const tasksList = tasksData.tasks || []
          const activeTask = tasksList.find(
            (t: { project_id: number; status: string; task_id: string }) =>
              t.project_id === currentProjectId &&
              (t.status === 'running' || t.status === 'pending')
          )

          if (activeTask) {
            try {
              await api.getParsingStatus(activeTask.task_id)
              message.error('Для этого проекта уже запущен парсинг')
              return
            } catch {
              console.warn(
                `Phantom task in API list, allowing new parsing: ${activeTask.task_id}`
              )
              clearTask(currentProjectId)
            }
          }
        } catch (error) {
          console.error('Error checking active tasks:', error)
        }
      }

      // Determine CRM ID
      let crmIdToUse: number
      let projectNameToUse: string

      if (selectedCrmId) {
        crmIdToUse = selectedCrmId
        projectNameToUse = projectName || `Проект ${selectedCrmId}`
      } else if (projectCrmId) {
        // Fallback to prop value if selectedCrmId was lost
        crmIdToUse = projectCrmId
        projectNameToUse = projectName || `Проект ${projectCrmId}`
      } else if (values?.project_id) {
        const selectedProject = projects.find((p) => p.id === values.project_id)
        if (!selectedProject) {
          message.error('Проект не найден')
          return
        }
        crmIdToUse = selectedProject.crm_id
        projectNameToUse = selectedProject.project_name
      } else {
        message.error('Проект не выбран')
        return
      }

      try {
        setStatusMessage('Запуск парсинга...')
        setCurrentStep(STEP_INDEX.parsing)

        const finalCredentialsId = credentialsId || autoCredentialsId
        if (!finalCredentialsId) {
          throw new Error('Нет доступных Yandex credentials')
        }

        const queries =
          selectedQueries.length > 0
            ? selectedQueries
            : queriesRegions?.queries && queriesRegions.queries.length > 0
              ? queriesRegions.queries
              : ['программирование для детей']

        let regionCodes: string[]
        if (selectedRegionCodes.length > 0) {
          regionCodes = selectedRegionCodes
        } else if (resolvedRegions && resolvedRegions.length > 0) {
          regionCodes = resolvedRegions
            .filter((r) => r.success && r.yandex_id)
            .map((r) => String(r.yandex_id))
        } else if (queriesRegions?.regions && queriesRegions.regions.length > 0) {
          regionCodes = queriesRegions.regions.map((r) => String(r.id ?? '225'))
        } else {
          regionCodes = ['225']
        }

        if (regionCodes.length === 0) {
          throw new Error('Не удалось определить регионы для парсинга')
        }

        if (queries.length === 0) {
          throw new Error('Не выбрано ни одного запроса')
        }

        const useBatch = queries.length > 1 || regionCodes.length > 1

        let response
        if (useBatch) {
          response = await api.startBatchParsing({
            crm_id: crmIdToUse,
            force_collect: false,
            credentials_id: finalCredentialsId,
            queries: queries,
            region_codes: regionCodes,
            excluded_words: [],
            min_clicks_threshold: minClicks,
            download_all_queries: downloadAll,
            top_queries_count: topQueries,
          })
        } else {
          response = await api.startParsing({
            crm_id: crmIdToUse,
            force_collect: false,
            credentials_id: finalCredentialsId,
            main_query: queries[0],
            region_code: regionCodes[0],
            excluded_words: [],
            min_clicks_threshold: minClicks,
            download_all_queries: downloadAll,
            top_queries_count: topQueries,
          })
        }

        if (!response.success) {
          throw new Error(response.error || response.message || 'Ошибка запуска парсинга')
        }

        const newTaskId = response.task_id
        if (!newTaskId) {
          throw new Error('Не получен task_id')
        }

        setTaskId(newTaskId)
        setStatusMessage('Парсинг запущен')
        message.success('Парсинг успешно запущен!')

        if (currentProjectId) {
          setActiveTask(currentProjectId, {
            task_id: newTaskId,
            project_id: currentProjectId,
            project_name: projectNameToUse,
            status: 'running',
            progress: 0,
            current_step: 'Запуск парсинга...',
            started_at: new Date().toISOString(),
          })
        }

        pollStatus(newTaskId)
      } catch (error: unknown) {
        setParsingFailed(true)
        const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка'
        setStatusMessage(errMsg)
        message.error('Ошибка запуска парсинга: ' + errMsg)
      }
    },
    [
      projectId,
      projectName,
      projectCrmId,
      selectedCrmId,
      projects,
      credentialsId,
      autoCredentialsId,
      selectedQueries,
      selectedRegionCodes,
      resolvedRegions,
      queriesRegions,
      minClicks,
      downloadAll,
      topQueries,
      clearTask,
      setActiveTask,
      pollStatus,
      setCurrentStep,
    ]
  )

  const resetParsing = useCallback(() => {
    stopParsingPoll()
    setTaskId(null)
    setProgress(0)
    setStatusMessage('')
    setParsingResult(null)
    setParsingFailed(false)
  }, [stopParsingPoll])

  return {
    taskId,
    progress,
    statusMessage,
    parsingResult,
    parsingFailed,
    setTaskId,
    setProgress,
    setStatusMessage,
    setParsingResult,
    setParsingFailed,
    pollStatus,
    handleStartParsing,
    stopParsingPoll,
    resetParsing,
    parsingPollRef,
  }
}
